import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Shield, Eye, EyeOff, Check, X, AlertCircle, AlertTriangle } from 'lucide-react';
import { manitobaDetachments } from '../data/detachments';
import AddressAutocomplete from '../components/AddressAutocomplete';

const Register = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    streetAddress: '',
    city: '',
    province: 'Manitoba',
    postalCode: '',
    nearestDetachment: '',
    numberOfCameras: '',
    cameraBrand: '',
    cameraLocation: '',
    coverageArea: '',
    consent: false,
    agreeTerms: false
  });

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    const password = formData.password;
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      longLength: password.length >= 12
    };
    
    if (checks.length) score += 1;
    if (checks.lowercase) score += 1;
    if (checks.uppercase) score += 1;
    if (checks.numbers) score += 1;
    if (checks.special) score += 1;
    if (checks.longLength) score += 1;
    
    let label, color;
    if (score <= 2) {
      label = language === 'en' ? 'Weak' : 'Faible';
      color = 'bg-red-500';
    } else if (score <= 4) {
      label = language === 'en' ? 'Medium' : 'Moyen';
      color = 'bg-yellow-500';
    } else {
      label = language === 'en' ? 'Strong' : 'Fort';
      color = 'bg-green-500';
    }
    
    return { score, label, color, checks, maxScore: 6 };
  }, [formData.password, language]);

  // Password match check
  const passwordsMatch = formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;
  const passwordsDontMatch = formData.confirmPassword && formData.password !== formData.confirmPassword;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: language === 'en' ? "Error" : "Erreur",
        description: language === 'en' ? "Passwords do not match" : "Les mots de passe ne correspondent pas",
        variant: "destructive"
      });
      return;
    }
    
    // Validate detachment is selected
    if (!formData.nearestDetachment) {
      toast({
        title: language === 'en' ? "Error" : "Erreur",
        description: t.detachmentRequired,
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Import API function
      const { registerUser } = await import('../services/api');
      
      // Submit to backend
      await registerUser(formData);
      
      toast({
        title: language === 'en' ? "Registration Successful!" : "Inscription Réussie!",
        description: language === 'en' 
          ? "Thank you for registering your security camera with Manitoba RCMP."
          : "Merci d'avoir enregistré votre caméra de surveillance auprès de la GRC du Manitoba.",
      });
      
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      toast({
        title: language === 'en' ? "Registration Failed" : "Échec de l'inscription",
        description: error.response?.data?.detail || (language === 'en' ? "An error occurred. Please try again." : "Une erreur s'est produite. Veuillez réessayer."),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              {t.createAccount}
            </h1>
          </div>

          {/* Emergency / crime-reporting notice */}
          <section className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4" aria-labelledby="register-emergency-heading">
            <div className="flex gap-3 items-start">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <h2 id="register-emergency-heading" className="text-base font-semibold text-red-900 mb-1">
                  {t.emergencyNoticeTitle}
                </h2>
                <p className="text-sm text-red-900 leading-relaxed">
                  {t.emergencyNotice}
                </p>
              </div>
            </div>
          </section>

          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{t.personalInfo}</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">{t.firstName}</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">{t.lastName}</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label htmlFor="email">{t.emailAddress}</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">{t.phone}</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label htmlFor="password">{t.password}</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={handleChange}
                          className="pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      
                      {/* Password Strength Indicator */}
                      {formData.password && (
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                                style={{ width: `${(passwordStrength.score / passwordStrength.maxScore) * 100}%` }}
                              />
                            </div>
                            <span className={`text-xs font-medium ${
                              passwordStrength.score <= 2 ? 'text-red-600' : 
                              passwordStrength.score <= 4 ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                              {passwordStrength.label}
                            </span>
                          </div>
                          
                          {/* Password Requirements */}
                          <div className="text-xs space-y-1">
                            <div className={`flex items-center gap-1 ${passwordStrength.checks?.length ? 'text-green-600' : 'text-gray-500'}`}>
                              {passwordStrength.checks?.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                              {language === 'en' ? 'At least 8 characters' : 'Au moins 8 caractères'}
                            </div>
                            <div className={`flex items-center gap-1 ${passwordStrength.checks?.uppercase ? 'text-green-600' : 'text-gray-500'}`}>
                              {passwordStrength.checks?.uppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                              {language === 'en' ? 'Uppercase letter (A-Z)' : 'Lettre majuscule (A-Z)'}
                            </div>
                            <div className={`flex items-center gap-1 ${passwordStrength.checks?.lowercase ? 'text-green-600' : 'text-gray-500'}`}>
                              {passwordStrength.checks?.lowercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                              {language === 'en' ? 'Lowercase letter (a-z)' : 'Lettre minuscule (a-z)'}
                            </div>
                            <div className={`flex items-center gap-1 ${passwordStrength.checks?.numbers ? 'text-green-600' : 'text-gray-500'}`}>
                              {passwordStrength.checks?.numbers ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                              {language === 'en' ? 'Number (0-9)' : 'Chiffre (0-9)'}
                            </div>
                            <div className={`flex items-center gap-1 ${passwordStrength.checks?.special ? 'text-green-600' : 'text-gray-500'}`}>
                              {passwordStrength.checks?.special ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                              {language === 'en' ? 'Special character (!@#$%^&*)' : 'Caractère spécial (!@#$%^&*)'}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="confirmPassword">{t.confirmPassword}</Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className={`pr-10 ${passwordsDontMatch ? 'border-red-500 focus:ring-red-500' : ''} ${passwordsMatch ? 'border-green-500 focus:ring-green-500' : ''}`}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      
                      {/* Password Match Indicator */}
                      {formData.confirmPassword && (
                        <div className={`mt-2 flex items-center gap-1 text-sm ${passwordsMatch ? 'text-green-600' : 'text-red-600'}`}>
                          {passwordsMatch ? (
                            <>
                              <Check className="w-4 h-4" />
                              {language === 'en' ? 'Passwords match' : 'Les mots de passe correspondent'}
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-4 h-4" />
                              {language === 'en' ? 'Passwords do not match' : 'Les mots de passe ne correspondent pas'}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{t.addressInfo}</h3>
                  <div>
                    <Label htmlFor="streetAddress">{t.streetAddress}</Label>
                    <AddressAutocomplete
                      value={formData.streetAddress}
                      onChange={(value) => setFormData(prev => ({ ...prev, streetAddress: value }))}
                      onAddressSelect={(address) => {
                        setFormData(prev => ({
                          ...prev,
                          streetAddress: address.streetAddress,
                          city: address.city || prev.city,
                          province: address.province || 'Manitoba',
                          postalCode: address.postalCode || prev.postalCode
                        }));
                      }}
                      placeholder={language === 'en' ? "Start typing your address..." : "Commencez à taper votre adresse..."}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {language === 'en' 
                        ? "Type to search for your address, or enter manually" 
                        : "Tapez pour rechercher votre adresse, ou entrez manuellement"}
                    </p>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <Label htmlFor="city">{t.city}</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="province">{t.province}</Label>
                      <Input
                        id="province"
                        name="province"
                        value={formData.province}
                        onChange={handleChange}
                        readOnly
                      />
                    </div>
                    <div>
                      <Label htmlFor="postalCode">{t.postalCode}</Label>
                      <Input
                        id="postalCode"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  
                  {/* Nearest Detachment Dropdown */}
                  <div className="mt-4">
                    <Label htmlFor="nearestDetachment">{t.nearestDetachment} *</Label>
                    <Select 
                      value={formData.nearestDetachment} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, nearestDetachment: value }))}
                      required
                    >
                      <SelectTrigger className="w-full mt-1">
                        <SelectValue placeholder={t.selectDetachment} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {manitobaDetachments.map((detachment) => (
                          <SelectItem key={detachment} value={detachment}>
                            {detachment}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Camera Information */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{t.cameraInfo}</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="numberOfCameras">{t.numberOfCameras}</Label>
                      <Input
                        id="numberOfCameras"
                        name="numberOfCameras"
                        type="number"
                        min="1"
                        value={formData.numberOfCameras}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="cameraBrand">{t.cameraBrand}</Label>
                      <Input
                        id="cameraBrand"
                        name="cameraBrand"
                        value={formData.cameraBrand}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label htmlFor="cameraLocation">{t.cameraLocation}</Label>
                    <Textarea
                      id="cameraLocation"
                      name="cameraLocation"
                      value={formData.cameraLocation}
                      onChange={handleChange}
                      rows={3}
                      required
                    />
                  </div>
                  <div className="mt-4">
                    <Label htmlFor="coverageArea">{t.coverageArea}</Label>
                    <Textarea
                      id="coverageArea"
                      name="coverageArea"
                      value={formData.coverageArea}
                      onChange={handleChange}
                      rows={3}
                      required
                    />
                  </div>
                </div>

                {/* Consent */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{t.consent}</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="consent"
                        name="consent"
                        checked={formData.consent}
                        onCheckedChange={(checked) => 
                          setFormData(prev => ({ ...prev, consent: checked }))
                        }
                        required
                      />
                      <Label htmlFor="consent" className="text-sm leading-relaxed">
                        {t.consentText}
                      </Label>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="agreeTerms"
                        name="agreeTerms"
                        checked={formData.agreeTerms}
                        onCheckedChange={(checked) => 
                          setFormData(prev => ({ ...prev, agreeTerms: checked }))
                        }
                        required
                      />
                      <Label htmlFor="agreeTerms" className="text-sm">
                        {t.agreeTerms}
                      </Label>
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t.submitting : t.submit}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Register;